import type { CategoryEntry, DocEntry, DocDetail, DocVersionDetail, CreateDocRequest, UpdateDocRequest, AuditLog, CyberpunkPhoto, UploadPhotoRequest, UpdatePhotoRequest } from './types';

const API_BASE = '/api';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    throw new Error(`API ${res.status}: ${path}`);
  }
  return res.json();
}

export const api = {
  // ── Categories ──────────────────────────────────────────────
  getCategories: () => fetchJSON<CategoryEntry[]>('/categories'),

  // ── Docs ────────────────────────────────────────────────────
  listDocs: (params?: { category?: string; status?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return fetchJSON<DocEntry[]>(`/docs${query ? '?' + query : ''}`);
  },

  getDoc: (slug: string) =>
    fetchJSON<DocDetail>(`/docs/${slug}`),

  // ── Auth ────────────────────────────────────────────────────
  login: (username: string, password: string) =>
    fetchJSON<{ token: string; expires_at: number }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  // ── Admin CRUD ──────────────────────────────────────────────
  createDoc: (data: CreateDocRequest, token: string) =>
    fetchJSON<{ slug: string }>('/docs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }),

  updateDoc: (slug: string, data: UpdateDocRequest, token: string) =>
    fetchJSON<{ message: string }>(`/docs/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }),

  deleteDoc: (slug: string, token: string) =>
    fetchJSON<{ message: string }>(`/docs/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),

  // ── Recycle Bin ────────────────────────────────────────────
  listRecycleBin: (token: string) =>
    fetchJSON<DocEntry[]>('/docs/recycle-bin', {
      headers: { Authorization: `Bearer ${token}` },
    }),

  restoreDoc: (slug: string, token: string) =>
    fetchJSON<{ message: string }>(`/docs/${encodeURIComponent(slug)}/restore`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }),

  // 回收站文档预览（读取软删文档完整内容）
  getDeletedDoc: (slug: string, token: string) =>
    fetchJSON<DocDetail>(`/docs/${encodeURIComponent(slug)}/deleted`, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // 版本快照预览（读取指定版本完整内容）
  getDocVersion: (slug: string, version: number, token: string) =>
    fetchJSON<DocVersionDetail>(`/docs/${encodeURIComponent(slug)}/versions/${version}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // 回滚文档到指定版本快照
  rollbackDoc: (slug: string, targetVersion: number, token: string) =>
    fetchJSON<{ slug: string; version: number }>(`/docs/${encodeURIComponent(slug)}/rollback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ target_version: targetVersion }),
    }),

  purgeDoc: (slug: string, token: string) =>
    fetchJSON<{ message: string }>(`/docs/${encodeURIComponent(slug)}/purge`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),

  batchPurgeDocs: (slugs: string[], token: string) =>
    fetchJSON<{ purged: number }>('/docs/batch/purge', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ slugs }),
    }),

  // ── Audit Logs ─────────────────────────────────────────────
  listAuditLogs: (params: { slug?: string; limit?: number; offset?: number }, token: string) => {
    const qs = new URLSearchParams();
    if (params.slug) qs.set('slug', params.slug);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return fetchJSON<AuditLog[]>(`/audit-logs${query ? '?' + query : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // ── Upload ──────────────────────────────────────────────────
  uploadFile: (slug: string, filename: string, contentBase64: string, token: string) =>
    fetchJSON<{ filename: string; path: string; size: number; url: string }>('/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ slug, filename, content_base64: contentBase64 }),
    }),

  // ── Cyberpunk Gallery（COS 动态接口）─────────────────────────
  // 公开：仅返回 active 照片
  listCyberpunkPhotos: () => fetchJSON<CyberpunkPhoto[]>('/photos'),

  // 管理端：返回全部状态（含 hidden/deleted），需鉴权
  listAllCyberpunkPhotos: (token: string) =>
    fetchJSON<CyberpunkPhoto[]>('/photos?all=true', {
      headers: { Authorization: `Bearer ${token}` },
    }),

  // 上传（服务端代理直传 COS 后入库），需鉴权
  uploadCyberpunkPhoto: (data: UploadPhotoRequest, token: string) =>
    fetchJSON<CyberpunkPhoto>('/photos/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }),

  // 更新元数据（title/description/sort_order/status），需鉴权
  updateCyberpunkPhoto: (id: number, patch: UpdatePhotoRequest, token: string) =>
    fetchJSON<CyberpunkPhoto>(`/photos/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(patch),
    }),

  // 软删（COS 对象保留），需鉴权
  deleteCyberpunkPhoto: (id: number, token: string) =>
    fetchJSON<{ message: string; id: number }>(`/photos/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// ── Asset URL helper ──────────────────────────────────────────
/**
 * Build the API URL for a file under docs/.
 * Example: getFileUrl("ai-docs/commit-standards", "assets/photo.jpg")
 *   → "/api/files/ai-docs/commit-standards/assets/photo.jpg"
 */
export function getFileUrl(docSlug: string, relativePath: string): string {
  return `/api/files/${docSlug}/${relativePath}`;
}
